import React, { useState, useEffect } from 'react';
import { useTabContext } from 'context/TabContext';
import { useLoading } from 'hooks/useLoading';
import { useExtraQuestionsData } from 'context/ExtraQuestionsContext';
import { post } from 'api/httpClient';
import { Questions } from 'types';
import { useHistory } from 'react-router-dom';
import { useSession } from 'context/SessionContext';
import { useUserSession } from 'context/UserContext';
import { fetchQuestions as handleFetchQuestions } from 'hooks/useFetchQuestions';

import Dialog from 'components/Modal/Dialog';
import { Button, useDisclosure } from '@chakra-ui/react';

interface UseQuestionnairePageProps {
  activityFilter: string;
  tabIndex: number;
}

const useQuestionnairePage = ({
  activityFilter,
  tabIndex,
}: UseQuestionnairePageProps) => {
  const { completeTab, activeTab, resolveEmptyTab, numberOfTabs, enabledTabs, emptyTabs, setActiveTab, setEmptyTabs } =
    useTabContext();
  const { loading, startLoading, stopLoading, LoadingSpinner } = useLoading();
  const { extraAnswers } = useExtraQuestionsData();
  const history = useHistory();
  const { data, activities } = useSession();
  const { userData } = useUserSession();

  const [subTabQuestions, setSubTabQuestions] = useState<Questions[]>([]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSaveAnswers = async (data: any, isTabEmpty?: boolean) => {
    try {
      startLoading();

      let professionCode = userData.professionCode;

      const profession = activities.filter(act => act.code === professionCode);
      const professionData = {
        professionCode,
        professionDescription: profession[0]?.description,
      };
      const newData = { ...data, ...professionData };
      
      newData.extraQuestions = (newData.extraQuestions as any[]).filter(
        d => d.type !== 'Questionário'
      );

      newData.encryptString = userData.encryptString;
      newData.nif = userData.nif;
      
      await post('/v1/Questionnaire/SaveAnswers', newData)
        .then(() => {
          history.push('/success');
        })
        .catch(error =>
          console.error('Houve erro ao enviar as respostas.', error),
        )
        .finally(() => stopLoading());
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
    } finally {
      stopLoading();
    }
  };

  const handleOpenConfirmationModal = () => {
    onOpen();
  }

  const handleOnCloseModal = async () => {
    const filteredTabs = enabledTabs.filter(tab => !emptyTabs.includes(tab));
    const lastTab = Math.max(...filteredTabs)
    setActiveTab(lastTab)
    onClose();
  };

  useEffect(() => {
    if (activeTab === tabIndex) {
      const filteredExtraAnswers = extraAnswers.filter(
        item => item.activity === activityFilter,
      );

      setEmptyTabs(prev => prev.filter(tab => tab !== activeTab));

      const fetchQuestions = async () => {
        startLoading();
        try {
          const promises = filteredExtraAnswers.map(item =>

            handleFetchQuestions(item.code, item.activity)
            .then(({ sections }) => ({
              sections,
            })),
          );

          const results: any = await Promise.all(promises);

          if (!results.length) {
            if (activeTab === numberOfTabs - 1) {
              onOpen();
            }

            resolveEmptyTab(tabIndex);
            return;
          }

          setSubTabQuestions(results);
        } catch (error) {
          console.error('Erro ao buscar as perguntas:', error);
        } finally {
          stopLoading();
        }
      };

      fetchQuestions();
    }
  }, [activeTab, extraAnswers]);

  const confirmationDialog = (
    <Dialog
      title={`Tem a certeza que pretende enviar o 
       seu questionário?`}
      isOpen={isOpen}
      onClose={() => handleOnCloseModal()}
      footerActions={
        <>
          <Button
            variant="outline"
            color="brand.fontColors.primary"
            mr={3}
            onClick={() => handleOnCloseModal()}
          >
            Cancelar
          </Button>
          <Button
            bg="#0092b0"
            _hover={{ bg: '#00a2c4' }}
            isLoading={loading}
            loadingText="A Finalizar"
            colorScheme="blue"
            onClick={() => handleSaveAnswers(data, true)}
          >
            Enviar
          </Button>
        </>
      }
    >
      <p>
        Por favor, confirme os seus dados. Depois de avançar não poderá fazer mais alterações.
      </p>
    </Dialog>
  );

  return {
    subTabQuestions,
    activeTab,
    completeTab,
    resolveEmptyTab,
    loading,
    startLoading,
    stopLoading,
    LoadingSpinner,
    handleSaveAnswers,
    confirmationDialog,
    handleOpenConfirmationModal,
    setSubTabQuestions
  };
};

export default useQuestionnairePage;
